import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import { useState } from "react";
import { FaPray, FaSearch } from "react-icons/fa";
import { cnamLookupMiddleware } from "../../../middleware/cnamMiddleware";
import { CnamLookupRequestInputDTO } from "../../../model/input/cnam/cnamLookupRequestInputDTO";
import { CnamRequestSubmissionBusinessesInputDTO } from "../../../model/input/cnam/cnamRequestSubmissionBusinessesInputDTO";
import { CnamLookupOutputDTO } from "../../../model/output/cnamLookupRequestOutputDTO";
import { errorActions } from "../../../store/errorSlice";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { isValidNsnOrCc1 } from "../../../utils/genericUtils";

interface LookupFormError {
  error: boolean;
  errorMessage?: string;
}

const CnamLookup = () => {
  const dispatch = useAppDispatch();
  const lookupResults = useAppSelector<CnamLookupOutputDTO[]>(
    (state) => state.cnam.cnamLookup
  );
  const [formErrorState, setFormErrorState] = useState<LookupFormError>({
    error: false,
  });
  const [lookupNumbers, setLookupNumbers] = useState<string>("");
  const [lookupLoading, setLookupLoading] = useState<boolean>(false);

  const performCnamLookup = async () => {
    if (!lookupNumbers.trim()) {
      setFormErrorState({
        error: true,
        errorMessage: "You must input Telephone numbers to query",
      });
      return;
    }

    const tns = lookupNumbers
      .split("\n")
      .filter((phone) => phone.trim() !== "");
    const invalidPhones = tns.filter((phone) => !isValidNsnOrCc1(phone.trim()));

    if (invalidPhones.length > 0) {
      setFormErrorState({
        error: true,
        errorMessage: `Invalid phone numbers: ${invalidPhones.join(", ")}`,
      });
      return;
    }

    if (tns.length > 100) {
      if (!lookupNumbers.trim()) {
        setFormErrorState({
          error: true,
          errorMessage: `Maximum 100 phone numbers per lookup`,
        });
        return;
      }
    }

    setFormErrorState({
      error: false,
    });

    const input: CnamLookupRequestInputDTO = { tns: tns };

    dispatch(cnamLookupMiddleware(input));
  };

  return (
    <>
      <Box>
        <Typography variant="h4" gutterBottom>
          Calling Name (CNAM) Lookup - View Current Results
        </Typography>
        <Typography variant="h6" sx={{ mb: 3, color: "text.secondary" }}>
          Enter phone numbers to view the current Calling Name (CNAM)
          information.
        </Typography>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              multiline
              rows={8}
              label="Phone Numbers (one per line)"
              placeholder="15551234567&#10;15559876543&#10;15555555555"
              value={lookupNumbers}
              onChange={(e) => setLookupNumbers(e.target.value)}
              helperText={
                formErrorState.error
                  ? formErrorState.errorMessage
                  : "Enter up to 100 phone numbers, one per line"
              }
              error={formErrorState.error}
            />
            <Button
              variant="contained"
              startIcon={<FaSearch />}
              onClick={performCnamLookup}
              disabled={lookupLoading}
              sx={{ mt: 2 }}
              fullWidth
            >
              {lookupLoading ? "Looking up..." : "Lookup CNAM"}
            </Button>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 2 }} elevation={4}>
              <Typography variant="h6" gutterBottom>
                Results ({lookupResults.length})
              </Typography>
              <TableContainer sx={{ maxHeight: "65vh" }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Phone Number</TableCell>
                      <TableCell>Current CNAM</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lookupResults.map((result, index) => (
                      <TableRow key={index}>
                        <TableCell sx={{ fontFamily: "monospace" }}>
                          {result.tn}
                        </TableCell>
                        <TableCell>{result.cnam || "NO DATA"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default CnamLookup;
